import { Link } from "react-router-dom";
import {
  ArrowRight, BookOpen, Brain, Highlighter, Trophy, Flame, GraduationCap, Sparkles,
  CheckCircle2, Users, ShieldCheck, Headphones, Crown,
} from "lucide-react";
import PageShell from "@/components/Layout/PageShell";
import Seo from "@/components/Seo";
import RelatedLinks from "@/components/RelatedLinks";

const HERO_IMG_1 =
  "https://images.unsplash.com/photo-1548690312-3b978c893df0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwyfHxkaXZlcnNlJTIwc3R1ZGVudHMlMjByZWFkaW5nfGVufDB8fHx8MTc3ODY1MDI1MHww&ixlib=rb-4.1.0&q=85";
const HERO_IMG_2 =
  "https://images.unsplash.com/photo-1509062522246-3755977927d7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxjb2xsYWJvcmF0aXZlJTIwY2xhc3Nyb29tJTIwaGlnaCUyMHNjaG9vbHxlbnwwfHx8fDE3Nzg2NTAyNTB8MA&ixlib=rb-4.1.0&q=85";
const HERO_IMG_3 =
  "https://images.unsplash.com/photo-1659635445678-341054fc514b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHw0fHxkaXZlcnNlJTIwc3R1ZGVudHMlMjByZWFkaW5nfGVufDB8fHx8MTc3ODY1MDI1MHww&ixlib=rb-4.1.0&q=85";
const HERO_IMG_4 =
  "https://images.unsplash.com/photo-1700680056842-20d9acc36e91?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHxzdHVkZW50JTIwcGFzc2luZyUyMGV4YW0lMjBzdWNjZXNzfGVufDB8fHx8MTc3ODY1MDI2MHww&ixlib=rb-4.1.0&q=85";

const features = [
  { icon: Highlighter, color: "bg-spark-lavender text-spark-indigo",
    title: "The Annotator",
    desc: "Highlight, take notes, and let the AI surface symbolism, theme, tone, and rhetoric in seconds." },
  { icon: Brain, color: "bg-spark-mint text-emerald-800",
    title: "Adaptive AI Coaching",
    desc: "Mr. Ramsey-style guidance that meets every learner where they are — rigorous, encouraging, and never intimidating." },
  { icon: BookOpen, color: "bg-spark-peach text-orange-700",
    title: "Reading Practice Library",
    desc: "Elementary through AP passages across mythology, memoir, fiction, rhetoric and more." },
  { icon: GraduationCap, color: "bg-spark-lavender text-spark-indigo",
    title: "SAT / ACT / AP Prep",
    desc: "Eliminate wrong answers like a pro with timed practice and strategy walkthroughs." },
  { icon: Trophy, color: "bg-spark-mint text-emerald-800",
    title: "Gamification & Badges",
    desc: "Earn SPARX points, keep streaks alive, and unlock badges like Inferencing Detective." },
  { icon: Headphones, color: "bg-spark-peach text-orange-700",
    title: "Vocabulary Builder",
    desc: "Tier 2, Tier 3, SAT, ACT and AP words in real context with quick recall games." },
];

const audiences = [
  "Elementary readers", "Middle school", "High school", "AP / SAT / ACT",
  "ELL learners", "Struggling readers", "Intervention groups",
  "Homeschool families", "Teachers", "Schools & districts",
];

const tiers = [
  {
    name: "Free", price: "$0", tag: "Start reading today",
    perks: [
      "Daily curated passages",
      "Limited AI coaching",
      "Streaks & SPARX points",
      "Up to 2 game modes",
    ],
    cta: "Start Free", to: "/register",
  },
  {
    name: "Premium", price: "$9.99", tag: "Most loved by students", featured: true,
    perks: [
      "Unlimited annotations & AI analysis",
      "Adaptive coaching & companion",
      "SAT / ACT / AP prep modules",
      "Fluency analysis & advanced analytics",
      "Full vocabulary & game library",
    ],
    cta: "Go Premium", to: "/register",
  },
  {
    name: "Teacher", price: "$19.99", tag: "Tools for the classroom",
    perks: [
      "Class & group management",
      "Assignments & standards alignment",
      "Real-time analytics",
      "Intervention groupings",
    ],
    cta: "Talk to Sales", to: "/contact",
  },
  {
    name: "Founder", price: "$6.99", tag: "First 327 readers · 12-month price lock", crown: true,
    perks: [
      "Founder Member badge (Crown)",
      "Price locked at $6.99/mo for 12 months",
      "Early access to upcoming features",
      "Everything in Premium",
    ],
    cta: "Become a Founder", to: "/pricing",
  },
];

export default function Landing() {
  return (
    <PageShell>
      <Seo
        title="Read with purpose. Think with precision."
        description="SparkRead is an adaptive AI-powered reading comprehension and test-taking skills platform for elementary, middle, high school, AP, SAT, and ACT students. Build vocabulary, fluency, literary analysis, and multiple-choice reasoning."
        keywords="reading comprehension practice, SAT reading preparation, ACT reading practice, AP English reading skills, test-taking strategies, multiple choice reading questions, vocabulary development, adaptive reading app, literacy platform, AI reading coach"
        path="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "EducationalApplication",
          "name": "SparkRead",
          "url": "https://sparkread.net",
          "applicationCategory": "EducationalApplication",
          "operatingSystem": "Web",
          "description": "Adaptive AI reading comprehension and test-taking skills platform.",
          "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" },
        }}
      />
      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-radial-lavender dark:bg-radial-lavender-dark pointer-events-none" />
        <div className="container-spark relative pt-16 pb-20 lg:pt-24 lg:pb-28">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 animate-fade-up">
              <span className="pill" data-testid="hero-eyebrow">
                <Sparkles size={14} /> Read with purpose. Think with precision.
              </span>
              <h1 className="mt-6 font-display text-5xl sm:text-6xl lg:text-[4.25rem] font-black leading-[1.02] tracking-tight">
                Build stronger{" "}
                <span className="relative inline-block">
                  <span className="relative z-10">reading</span>
                  <span className="absolute left-0 right-0 bottom-1 h-3 bg-spark-mint -z-0 rounded-full" />
                </span>{" "}
                and test-taking skills.
              </h1>
              <p className="mt-6 text-lg leading-relaxed text-muted-foreground max-w-2xl">
                SparkRead helps elementary, middle, and high schoolers grow comprehension,
                vocabulary, fluency, literary analysis, and multiple-choice strategy through
                adaptive practice and AI-guided coaching from Mr. Ramsey.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/register" className="btn-primary" data-testid="hero-cta-start-free">
                  Start Reading Free <ArrowRight size={18} />
                </Link>
                <Link to="/practice" className="btn-ghost" data-testid="hero-cta-practice">
                  Practice Reading Skills
                </Link>
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-5 text-sm text-muted-foreground">
                <span className="inline-flex items-center gap-2"><CheckCircle2 size={16} className="text-emerald-500" /> No credit card</span>
                <span className="inline-flex items-center gap-2"><Users size={16} className="text-spark-purple" /> Grades K-12 + AP</span>
                <span className="inline-flex items-center gap-2"><ShieldCheck size={16} className="text-spark-indigo dark:text-spark-lavender" /> District-ready</span>
              </div>
            </div>

            {/* Bento image stack */}
            <div className="lg:col-span-5">
              <div className="grid grid-cols-6 grid-rows-6 gap-3 h-[460px] sm:h-[520px]">
                <div className="col-span-4 row-span-4 rounded-3xl overflow-hidden border border-border shadow-2xl">
                  <img loading="lazy" decoding="async" src={HERO_IMG_1} alt="Diverse student reading a book" className="h-full w-full object-cover" />
                </div>
                <div className="col-span-2 row-span-3 rounded-3xl overflow-hidden border border-border bg-spark-mint">
                  <img loading="lazy" decoding="async" src={HERO_IMG_2} alt="Collaborative classroom" className="h-full w-full object-cover" />
                </div>
                <div className="col-span-2 row-span-3 rounded-3xl overflow-hidden border border-border">
                  <img loading="lazy" decoding="async" src={HERO_IMG_4} alt="Student succeeding on exam" className="h-full w-full object-cover" />
                </div>
                <div className="col-span-4 row-span-2 rounded-3xl border border-border bg-card p-4 flex items-center gap-3">
                  <div className="grid h-12 w-12 place-items-center rounded-2xl bg-spark-purple text-white">
                    <Flame />
                  </div>
                  <div>
                    <div className="text-sm font-bold">Daily Streak System</div>
                    <div className="text-xs text-muted-foreground">SPARX points, badges, leaderboards</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TODAY'S SPARK — Release 1.1 acquisition surface */}
      <section className="container-spark py-16" data-testid="section-todays-spark">
        <div className="rounded-3xl border border-spark-purple/30 bg-gradient-to-br from-spark-purple/10 via-transparent to-transparent p-8 sm:p-12">
          <div className="grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 pill bg-spark-purple/10 border border-spark-purple/30">
                <Flame size={12} className="text-spark-purple" />
                <span className="text-xs font-bold uppercase tracking-widest text-spark-purple">Today’s Spark</span>
              </div>
              <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight" data-testid="todays-spark-heading">
                Today’s Reading Challenge
              </h2>
              <p className="mt-3 text-base sm:text-lg text-muted-foreground max-w-xl">
                Choose your grade and complete today’s free reading challenge in
                approximately five to seven minutes.
              </p>
              <div className="mt-5 flex flex-wrap gap-4 text-sm text-muted-foreground">
                <span className="inline-flex items-center gap-1">
                  <CheckCircle2 size={14} className="text-emerald-500" /> No account required
                </span>
                <span className="inline-flex items-center gap-1">
                  <ShieldCheck size={14} className="text-spark-indigo" /> Grades 3-12
                </span>
                <span className="inline-flex items-center gap-1">
                  <ArrowRight size={14} className="text-spark-purple" /> Premium Founder experience available
                </span>
              </div>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link to="/daily-spark" className="btn-primary" data-testid="todays-spark-cta">
                  Start today’s challenge <ArrowRight size={18} />
                </Link>
                <Link to="/pricing" className="btn-ghost" data-testid="todays-spark-founder-link">
                  See Founder benefits
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="rounded-2xl border border-border bg-card p-6 shadow-md">
                <div className="text-xs font-semibold uppercase tracking-wider text-spark-purple">
                  How it works
                </div>
                <ol className="mt-3 space-y-3 text-sm">
                  <li className="flex gap-3"><span className="grid h-6 w-6 place-items-center rounded-full bg-spark-purple text-white text-xs font-bold">1</span><span>Pick your grade — 3 through 12.</span></li>
                  <li className="flex gap-3"><span className="grid h-6 w-6 place-items-center rounded-full bg-spark-purple text-white text-xs font-bold">2</span><span>Read today’s curated passage.</span></li>
                  <li className="flex gap-3"><span className="grid h-6 w-6 place-items-center rounded-full bg-spark-purple text-white text-xs font-bold">3</span><span>Answer comprehension questions.</span></li>
                  <li className="flex gap-3"><span className="grid h-6 w-6 place-items-center rounded-full bg-spark-purple text-white text-xs font-bold">4</span><span>See a full explanation for every answer choice.</span></li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT SPARKREAD IS */}
      <section className="container-spark py-20" data-testid="section-what-is">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-spark-purple">What SparkRead Is</div>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold">An adaptive literacy ecosystem for every reader.</h2>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              A learning environment that strengthens comprehension, vocabulary, fluency, literary analysis,
              annotation skills, test-taking strategy, critical thinking, and academic confidence — all in one place.
            </p>
            <p className="mt-4 font-hand text-2xl text-spark-purple">
              “Read with purpose. Think with precision.”
            </p>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {features.map((f) => (
              <div key={f.title} className="card-spark p-6 hover:-translate-y-1" data-testid={`feature-${f.title.replace(/\s+/g, "-").toLowerCase()}`}>
                <div className={`inline-grid h-11 w-11 place-items-center rounded-2xl ${f.color}`}>
                  <f.icon size={20} />
                </div>
                <div className="mt-4 font-bold text-lg">{f.title}</div>
                <div className="mt-1 text-sm text-muted-foreground leading-relaxed">{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section className="container-spark py-20" data-testid="section-who">
        <div className="rounded-3xl bg-spark-lavender/60 dark:bg-card border border-border p-8 md:p-12 relative overflow-hidden">
          <div className="grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-6">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-spark-indigo dark:text-spark-lavender">
                Who SparkRead Is For
              </div>
              <h2 className="mt-3 text-3xl sm:text-4xl font-bold">
                A welcoming home for every reader, classroom, and family.
              </h2>
              <p className="mt-4 text-muted-foreground leading-relaxed max-w-xl">
                SparkRead reflects the readers it serves — diverse backgrounds, languages, learning
                styles, and reading levels. Whether you're decoding sight words or analyzing rhetoric,
                you'll find a path tuned for you.
              </p>
              <div className="mt-6 flex flex-wrap gap-2">
                {audiences.map((a) => (
                  <span key={a} className="pill bg-white dark:bg-secondary">{a}</span>
                ))}
              </div>
            </div>
            <div className="lg:col-span-6">
              <div className="rounded-3xl overflow-hidden border border-border shadow-xl">
                <img loading="lazy" decoding="async" src={HERO_IMG_3} alt="Independent study" className="w-full h-[360px] object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MORE THAN READING PRACTICE */}
      <section className="container-spark py-20" data-testid="section-more-than">
        <div className="max-w-3xl">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-spark-purple">More Than Reading Practice</div>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold">
            We teach students <em className="not-italic underline decoration-spark-mintDark decoration-[6px] underline-offset-4">how to think</em> through the question.
          </h2>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            Analyze passages, eliminate wrong answer choices, identify evidence, annotate strategically,
            and reason like a confident test-taker. Built for adaptive growth from foundational skills
            to AP-level rhetorical analysis.
          </p>
        </div>
        <div className="mt-10 grid md:grid-cols-3 gap-4">
          {[
            { k: "12+", v: "Reading skill domains" },
            { k: "K-12 + AP", v: "Adaptive grade bands" },
            { k: "AI", v: "Adaptive Coaching" },
          ].map((s) => (
            <div key={s.v} className="card-spark p-6">
              <div className="font-display text-4xl font-black text-spark-purple">{s.k}</div>
              <div className="mt-2 text-sm text-muted-foreground">{s.v}</div>
            </div>
          ))}
        </div>
      </section>

      {/* PRICING */}
      <section className="container-spark py-20" data-testid="section-pricing">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-spark-purple">Plans</div>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold">Plans that grow with every reader.</h2>
          </div>
          <Link to="/pricing" className="btn-ghost text-sm">See full comparison <ArrowRight size={16} /></Link>
        </div>
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {tiers.map((t) => (
            <div
              key={t.name}
              className={`card-spark p-6 relative ${
                t.featured ? "ring-2 ring-spark-purple shadow-[0_30px_80px_-30px_rgba(91,77,255,0.35)]" : ""
              } ${t.crown ? "ring-2 ring-amber-400/60" : ""}`}
              data-testid={`pricing-${t.name.toLowerCase()}`}
            >
              {t.featured && (
                <div className="absolute -top-3 left-6 pill bg-spark-purple text-white">
                  <Sparkles size={12} /> Most popular
                </div>
              )}
              {t.crown && (
                <div className="absolute -top-3 left-6 pill bg-amber-500 text-white" data-testid={`founder-badge-${t.name.toLowerCase()}`}>
                  <Crown size={12} /> Founder
                </div>
              )}
              <div className="text-sm font-semibold">{t.name}</div>
              <div className="mt-2 font-display text-4xl font-black">{t.price}<span className="text-base font-medium text-muted-foreground">/mo</span></div>
              <div className="mt-1 text-xs text-muted-foreground">{t.tag}</div>
              <ul className="mt-5 space-y-2 text-sm">
                {t.perks.map((p) => (
                  <li key={p} className="flex gap-2 items-start">
                    <CheckCircle2 size={16} className="text-emerald-500 mt-0.5 shrink-0" /> <span>{p}</span>
                  </li>
                ))}
              </ul>
              <Link
                to={t.to}
                className={`mt-6 w-full text-sm ${
                  t.crown ? "btn-primary bg-amber-500 hover:bg-amber-600"
                  : t.featured ? "btn-primary" : "btn-ghost"
                }`}
                data-testid={`pricing-cta-${t.name.toLowerCase()}`}
              >
                {t.cta}
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* INTERNAL LINK GRAPH (SEO) */}
      <RelatedLinks
        title="Explore SparkRead"
        items={[
          { to: "/practice", title: "Reading Practice Library", desc: "Adaptive passages with comprehension quizzes." },
          { to: "/test-lab", title: "Test-Taking Skills Lab", desc: "Eliminate wrong answers and reason like a pro." },
          { to: "/sat-prep", title: "SAT / ACT / AP Prep", desc: "Timed practice and strategy walkthroughs." },
          { to: "/vocabulary", title: "Vocabulary Builder", desc: "Tier 2, SAT, ACT, and AP words in context." },
          { to: "/annotator", title: "The Annotator", desc: "AI-guided literary analysis on every passage." },
          { to: "/insights", title: "Parent & Teacher Insights", desc: "Real analytics for classrooms and families." },
        ]}
      />

      {/* FINAL CTA */}
      <section className="container-spark pb-20">
        <div className="rounded-3xl p-10 md:p-16 relative overflow-hidden bg-gradient-to-br from-spark-purple via-[#6E5FFF] to-spark-indigo text-white">
          <div className="relative max-w-3xl">
            <h2 className="text-3xl sm:text-4xl font-bold leading-tight">
              Your next reading breakthrough starts today.
            </h2>
            <p className="mt-4 text-white/85">
              Build streaks, earn SPARX points, and grow with a coach who actually gets you.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register" className="inline-flex items-center gap-2 rounded-full bg-white text-spark-indigo px-6 py-3 font-semibold hover:-translate-y-0.5 transition" data-testid="final-cta-register">
                Start Reading Free <ArrowRight size={16} />
              </Link>
              <Link to="/practice" className="inline-flex items-center gap-2 rounded-full border border-white/40 px-6 py-3 font-semibold hover:bg-white/10 transition" data-testid="final-cta-practice">
                Browse the Library
              </Link>
            </div>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
